import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useFonts } from 'expo-font';
import {
  DMSans_400Regular,
  DMSans_500Medium,
} from '@expo-google-fonts/dm-sans';
import { IBMPlexMono_500Medium } from '@expo-google-fonts/ibm-plex-mono';
import { AuthProvider } from './src/context/AuthContext';
import { ApiKeyProvider } from './src/context/ApiKeyContext';
import RootNavigator, { ThemedNavigationContainer } from './src/navigation';
import { linking } from './src/navigation/linking';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Provider } from 'react-redux';
import { store } from './src/store';
import { ThemeProvider } from './src/styles/ThemeProvider';
import { PostHogProvider, usePostHog } from './src/lib/posthog';
import { initAnalytics } from './src/utils/analytics';
import { APP_VARIANT, IS_STAGING } from './src/config/appVariant';
import StagingBanner from './src/components/StagingBanner';

// ── Sentry init ──────────────────────────────────────────────────────
const SENTRY_DSN = Constants.expoConfig?.extra?.EXPO_PUBLIC_SENTRY_DSN || '';

Sentry.init({
  dsn: SENTRY_DSN,
  // Disable in dev to avoid noise; still captures in __DEV__ if DSN is set
  enabled: !__DEV__,
  // Performance monitoring — sample 20% of transactions in production
  tracesSampleRate: __DEV__ ? 1.0 : 0.2,
  // Send session replay data for 10% of sessions (crash replays always sent)
  _experiments: {
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  },
  // Attach user context automatically
  sendDefaultPii: false,
  // Environment tag
  environment: __DEV__ ? 'development' : APP_VARIANT,
});

// ── Analytics bridge ─────────────────────────────────────────────────
// Connects the PostHog client to the analytics module once mounted.
function AnalyticsBridge() {
  const posthog = usePostHog();
  useEffect(() => {
    initAnalytics(posthog);
  }, [posthog]);
  return null;
}

// ── App ──────────────────────────────────────────────────────────────
function App() {
  const [fontsLoaded] = useFonts({
    fontBody: DMSans_400Regular,
    fontBodyMedium: DMSans_500Medium,
    fontMono: IBMPlexMono_500Medium,
    // Dedicated key for the brand-mark glyph so it can't accidentally
    // inherit a font swap on `fontBodyMedium`. The logo lives outside the
    // design system on purpose — it's brand identity, not typography.
    fontBrandLogo: DMSans_500Medium,
  });

  if (!fontsLoaded) return null;

  return (
    <Provider store={store}>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <SafeAreaProvider>
          <PostHogProvider>
            <ThemeProvider>
              <AuthProvider>
                <ApiKeyProvider>
                  <AnalyticsBridge />
                  <ThemedNavigationContainer linking={linking}>
                    <RootNavigator />
                  </ThemedNavigationContainer>
                  {IS_STAGING && <StagingBanner />}
                </ApiKeyProvider>
              </AuthProvider>
            </ThemeProvider>
          </PostHogProvider>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </Provider>
  );
}

// Wrap with Sentry's error boundary for automatic crash capture
export default Sentry.wrap(App);
